'use client';

import { useEffect, useSyncExternalStore } from 'react';
import type { MedicalData } from '@/lib/types';
import type { ProfileApi } from '@/lib/api';

/**
 * One person's medical card, fetched once and shared by the card on the
 * page and the page in the sheet, so a save in the sheet shows on the card
 * behind it at once (each used to hold its own copy; the card only learned
 * of a change on reload). Same shape as `subscriptionsStore`.
 */
type Entry = MedicalData | 'error';
const cache = new Map<number, Entry>();
const pending = new Set<number>();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function setMedical(contactId: number, value: Entry) {
  cache.set(contactId, value);
  emit();
}

/** Patch the loaded data in place; a store still loading or in error is left alone. */
export function updateMedical(contactId: number, fn: (d: MedicalData) => MedicalData) {
  const cur = cache.get(contactId);
  if (!cur || cur === 'error') return;
  setMedical(contactId, fn(cur));
}

function load(api: ProfileApi, contactId: number) {
  if (pending.has(contactId)) return;
  pending.add(contactId);
  api
    .getMedical(contactId)
    .then((d) => setMedical(contactId, d))
    // A 401 here is a session problem, not "no medical records": say it failed.
    .catch(() => setMedical(contactId, 'error'))
    .finally(() => pending.delete(contactId));
}

/** Forget what we have and fetch again ("Try again"). */
export function refetchMedical(api: ProfileApi, contactId: number) {
  cache.delete(contactId);
  emit();
  load(api, contactId);
}

/** The cached entry, fetching it the first time anyone asks. `null` while loading. */
export function useMedical(api: ProfileApi, contactId: number): Entry | null {
  const entry = useSyncExternalStore(
    subscribe,
    () => cache.get(contactId) ?? null,
    () => null
  );
  useEffect(() => {
    if (cache.has(contactId)) return;
    load(api, contactId);
  }, [api, contactId]);
  return entry;
}
